import React from "react";
import {
  ActivityIndicator,
  FlatList,
  SafeAreaView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { Room } from "../../../../types/types";
import Illustration2 from "../../../../assets/Illustration2";
import MeetingOption from "../components/MeetingOption";
import CreateMeetingButton from "../components/CreateMeetingButton";

type ItemVM = {
  room: Room;
  participantsCount: number;
  isSelected: boolean;
  showFullVisual: boolean;
  isDisabled: boolean;
};

type CreateItem = {
  kind: "create";
  id: string;
};

type ListItem = ItemVM | CreateItem;

type Props = {
  title: string;
  subtitle: string;
  loading: boolean;
  errorText: string | null;
  emptyText: string;
  items: ItemVM[];
  onSelectRoom: (room: Room) => void;
  onPressCreateMeeting: () => void;
};

export default function MyMeetingsViewMobile({
  loading,
  errorText,
  items,
  onSelectRoom,
  onPressCreateMeeting,
}: Props) {
  const listItems: ListItem[] = items.length
    ? [{ kind: "create", id: "create" }, ...items]
    : items;

  return (
    <SafeAreaView style={styles.container}>
      {loading && <ActivityIndicator size="large" style={styles.loading} />}
      {errorText && <Text style={styles.errorText}>{errorText}</Text>}

      <FlatList
        data={listItems}
        keyExtractor={(it) => ("kind" in it ? it.id : it.room.id)}
        contentContainerStyle={styles.listContainer}
        renderItem={({ item }) =>
          "kind" in item ? (
            <View style={styles.createCard}>
              <CreateMeetingButton
                onPress={onPressCreateMeeting}
                size={47}
                lineLength={25.99}
                lineThickness={4.41}
              />
              <Text style={styles.createCardText}>
                Create a new conversation
              </Text>
            </View>
          ) : (
            <View style={styles.cardWrapper}>
              <MeetingOption
                room={item.room}
                isSelected={item.isSelected}
                onSelect={() => onSelectRoom(item.room)}
                participantsCount={item.participantsCount}
                showFullVisual={item.showFullVisual}
                isDisabled={item.isDisabled}
                showDisabledState={false}
                disabledActionLabel="EDIT CONVERSATION"
              />
            </View>
          )
        }
        ListEmptyComponent={
          !loading ? (
            <View style={styles.emptyContainer}>
              <Illustration2 width={307} height={268} />
              <View style={styles.textBlock}>
                <Text style={styles.emptyTitle}>All My Conversations</Text>
                <Text style={styles.emptyText}>
                  You haven&#39;t created or joined any conversations yet. You
                  can start or join one when you feel ready
                </Text>
              </View>
              <CreateMeetingButton
                onPress={onPressCreateMeeting}
                size={47}
                lineLength={25.99}
                lineThickness={4.41}
              />
            </View>
          ) : null
        }
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F7F9",
  },
  loading: {
    marginTop: 20,
  },
  errorText: {
    color: "red",
    textAlign: "center",
    fontFamily: "Lexend",
    marginTop: 12,
  },
  listContainer: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 32,
    flexGrow: 1,
  },
  cardWrapper: {
    width: "100%",
    maxWidth: 343,
    alignSelf: "center",
  },
  createCard: {
    width: "100%",
    maxWidth: 343,
    alignSelf: "center",
    minHeight: 195,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#D8DBE1",
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    marginBottom: 16,
  },
  createCardText: {
    fontSize: 14,
    lineHeight: 14,
    fontFamily: "LexendBold",
    textTransform: "uppercase",
    color: "#3A6FB1",
    textAlign: "center",
  },
  emptyContainer: {
    flexGrow: 1,
    alignItems: "center",
    paddingTop: 48,
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 38,
  },
  textBlock: {
    width: "100%",
    maxWidth: 343,
    alignItems: "center",
    gap: 16,
  },
  emptyTitle: {
    fontSize: 26,
    lineHeight: 28,
    fontFamily: "LexendBold",
    color: "#2B2B2F",
    textAlign: "center",
  },
  emptyText: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: "Lexend",
    color: "#2B2B2F",
    textAlign: "center",
    opacity: 0.6,
  },
});
